import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { Auth0Provider } from '@auth0/auth0-react';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { ModuleRegistry } from '@ag-grid-community/core';
import { CsvExportModule } from '@ag-grid-community/csv-export';
import { HelmetProvider } from 'react-helmet-async';
import { readSoftwareIqAnalyticsConsent } from '@softwareiq/brand-ui';
import { store } from './app/store';
import App from './App';
import '@fontsource-variable/plus-jakarta-sans';
import '@fontsource-variable/plus-jakarta-sans/wght-italic.css';
import '@softwareiq/brand-ui/styles.css';
import './index.css';
import { AUTH0_AUDIENCE } from './constants/api';

// Consent defaults must exist before either application queues GA configuration or events.
try {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
  window.gtag('consent', 'default', {
    analytics_storage: readSoftwareIqAnalyticsConsent() === 'accepted' ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });
  window.gtag('set', {
    ads_data_redaction: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
} catch (error) {
  console.warn('Unable to initialize analytics consent defaults.', error);
}

// AG Grid modular setup
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  CsvExportModule
]);

/* ── Auth0 env vars ─────────────────────────────────────────────── */
const domain   = process.env.REACT_APP_AUTH0_DOMAIN;
const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;

if (!domain || !clientId) {
  if (process.env.NODE_ENV === 'production') {
    throw new Error('Auth0 configuration missing. Check REACT_APP_AUTH0_DOMAIN and REACT_APP_AUTH0_CLIENT_ID');
  }
  console.warn('Auth0 configuration missing. Auth features will not work.');
}

/* ── SSR-safe redirect URI ───────────────────────────────────────── */
const getRedirectUri = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin + '/callback';
  }
  return 'https://research.softwareiq.io/callback';
};

/* ── Persist the user's intent (trial / pay) after Auth0 redirects ─ */
const onRedirectCallback = (appState) => {
  if (appState?.requestedTier) {
    // e.g. 'trial'  |  'pay'
    localStorage.setItem('requestedTier', appState.requestedTier);
  }

  //  If you pass other navigation info (returnTo), honour it here:
  if (appState?.returnTo) {
    window.history.replaceState({}, document.title, appState.returnTo);
  }
};

/* ── App with Providers ───────────────────────────────────────────── */
const AppWithProviders = () => (
  <HelmetProvider>
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri:   getRedirectUri(),
        audience:       AUTH0_AUDIENCE,
        scope:          'openid profile email offline_access',
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      onRedirectCallback={onRedirectCallback}
    >
      <Provider store={store}>
        <App />
      </Provider>
    </Auth0Provider>
  </HelmetProvider>
);

/* ── Render the application ─────────────────────────────────────────── */
const rootElement = document.getElementById('root');
createRoot(rootElement).render(<AppWithProviders />);
